import {
  BoxGeometry,
  EdgesGeometry,
  Group,
  LineBasicMaterial,
  LineSegments,
  OctahedronGeometry,
} from 'three';
import { BALANCE } from '../config/balance';
import type { BuildPlacement } from '../builder/BuildController';

const VALID_COLOR = 0x63f0d0;
const INVALID_COLOR = 0xff6a4a;
const DRONE_COLOR = 0xffe2a8;
const MAGNETIC_COLOR = 0x9ff6ff;
const WAYPOINT_COLOR = 0xffb347;

/**
 * The scanner tool: a floating outline of whatever the player is about to put
 * down, sitting exactly where it would land.
 *
 * It is a preview, not a model, so it is drawn as wireframe edges: the outline
 * reads against every surface, and a legal spot and an illegal one are told
 * apart by colour alone.
 */
export class BuildHologram {
  readonly group = new Group();

  private readonly boxEdges: LineSegments;
  private readonly boxMaterial = new LineBasicMaterial({
    color: VALID_COLOR,
    transparent: true,
    opacity: 0.9,
  });
  private readonly droneEdges: LineSegments;
  private readonly droneMaterial = new LineBasicMaterial({
    color: DRONE_COLOR,
    transparent: true,
    opacity: 0.9,
  });
  private readonly waypointEdges: LineSegments;
  private readonly waypointMaterial = new LineBasicMaterial({
    color: WAYPOINT_COLOR,
    transparent: true,
    opacity: 0.95,
  });

  constructor() {
    this.boxEdges = new LineSegments(new EdgesGeometry(new BoxGeometry(1, 1, 1)), this.boxMaterial);
    this.droneEdges = new LineSegments(
      new EdgesGeometry(new OctahedronGeometry(0.55)),
      this.droneMaterial,
    );
    this.waypointEdges = new LineSegments(
      new EdgesGeometry(new OctahedronGeometry(0.3)),
      this.waypointMaterial,
    );
    this.group.add(this.boxEdges, this.droneEdges, this.waypointEdges);
    this.group.visible = false;
  }

  /** Show the placement under the crosshair, or hide the scanner entirely. */
  update(placement: BuildPlacement | null, magnetic = false): void {
    if (!placement) {
      this.group.visible = false;
      return;
    }
    this.group.visible = true;
    const isDrone = placement.kind === 'drone';
    const isWaypoint = placement.kind === 'waypoint';
    this.boxEdges.visible = !isDrone && !isWaypoint;
    this.droneEdges.visible = isDrone;
    this.waypointEdges.visible = isWaypoint;

    if (isWaypoint) {
      // A waypoint is a small marker on the surface it was aimed at, so it is
      // lifted just off that surface to stay readable against it.
      this.waypointEdges.position.set(
        placement.position[0] + placement.normal[0] * 0.25,
        placement.position[1] + placement.normal[1] * 0.25,
        placement.position[2] + placement.normal[2] * 0.25,
      );
      const diameter = Math.max(0.2, placement.size[0]);
      this.waypointEdges.scale.setScalar(diameter / 0.6);
      this.waypointMaterial.color.setHex(placement.valid ? WAYPOINT_COLOR : INVALID_COLOR);
      return;
    }

    if (isDrone) {
      // The marker sits where the drone will hover — the same height the drone
      // itself hovers at — so the preview reads against the anchor it is placed
      // on rather than the surface point.
      this.droneEdges.position.set(
        placement.position[0],
        placement.position[1] + BALANCE.combat.droneHoverHeight,
        placement.position[2],
      );
      const color = magnetic ? MAGNETIC_COLOR : DRONE_COLOR;
      this.droneMaterial.color.setHex(placement.valid ? color : INVALID_COLOR);
      return;
    }

    this.boxEdges.position.set(...placement.position);
    this.boxEdges.scale.set(...placement.size);
    this.boxMaterial.color.setHex(placement.valid ? VALID_COLOR : INVALID_COLOR);
  }

  setVisible(visible: boolean): void {
    if (!visible) this.group.visible = false;
  }

  dispose(): void {
    this.boxEdges.geometry.dispose();
    this.droneEdges.geometry.dispose();
    this.waypointEdges.geometry.dispose();
    this.boxMaterial.dispose();
    this.droneMaterial.dispose();
    this.waypointMaterial.dispose();
  }
}
